import { useCallback, useRef, useState } from 'react';
import {
  exportBlocker,
  ExportCancelled,
  exportPieceToMp4,
  FAILURE_TITLE,
  TaintedSources,
  type ExportNotice
} from '../export/export-piece';
import type { PieceModel } from '../../model/types';
import { exportedVideoName } from '../../shared/utils/exported-video-name';

export type RecordingMode = 'export' | 'save';

type UseVideoExportParams = {
  /** Authoritative piece at export time. */
  getModel: () => PieceModel;
};

type UseVideoExportReturn = {
  /** Produces the video and downloads it. */
  exportVideo: (onComplete?: (blob: Blob) => void) => void;
  /** Produces the video and returns the Blob without downloading. */
  recordVideo: () => Promise<Blob>;
  cancelVideoExport: () => void;
  isExportingVideo: boolean;
  progress: number;
  recordingMode: RecordingMode;
  exportNotice: ExportNotice | null;
  dismissExportNotice: () => void;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const noticeFor = (error: unknown): ExportNotice | null => {
  if (error instanceof ExportCancelled) return null;
  if (error instanceof TaintedSources) return { title: FAILURE_TITLE, reason: error.message };

  console.error('Export failed', error);
  return { title: FAILURE_TITLE };
};

/**
 * Prints the piece as an MP4, frame by frame from the model. Nothing here reads
 * the clock, so a slow machine or a hidden tab makes it take longer, never wrong.
 */
export const useVideoExport = ({ getModel }: UseVideoExportParams): UseVideoExportReturn => {
  const [isExportingVideo, setIsExportingVideo] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recordingMode, setRecordingMode] = useState<RecordingMode>('export');
  const [exportNotice, setExportNotice] = useState<ExportNotice | null>(null);

  const cancelledRef = useRef(false);

  const print = useCallback(async (model: PieceModel): Promise<Blob> => {
    cancelledRef.current = false;
    setExportNotice(null);
    setIsExportingVideo(true);
    setProgress(0);

    // One state update per percent instead of one per frame.
    let lastPercent = -1;

    try {
      return await exportPieceToMp4({
        model,
        isCancelled: () => cancelledRef.current,
        onProgress: value => {
          const percent = Math.floor(value * 100);
          if (percent === lastPercent) return;
          lastPercent = percent;
          setProgress(value);
        }
      });
    } finally {
      setIsExportingVideo(false);
      setProgress(0);
    }
  }, []);

  const exportVideo = useCallback(
    async (onComplete?: (blob: Blob) => void) => {
      const model = getModel();
      setRecordingMode('export');

      const blocked = exportBlocker(model);
      if (blocked) {
        setExportNotice(blocked);
        return;
      }

      try {
        const blob = await print(model);
        downloadBlob(blob, exportedVideoName());
        onComplete?.(blob);
      } catch (error) {
        setExportNotice(noticeFor(error));
      }
    },
    [getModel, print]
  );

  const recordVideo = useCallback(async (): Promise<Blob> => {
    const model = getModel();
    setRecordingMode('save');

    const blocked = exportBlocker(model);
    if (blocked) {
      setExportNotice(blocked);
      throw new Error(blocked.title);
    }

    try {
      return await print(model);
    } catch (error) {
      setExportNotice(noticeFor(error));
      throw error;
    }
  }, [getModel, print]);

  const cancelVideoExport = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const dismissExportNotice = useCallback(() => setExportNotice(null), []);

  return {
    exportVideo,
    recordVideo,
    cancelVideoExport,
    isExportingVideo,
    progress,
    recordingMode,
    exportNotice,
    dismissExportNotice
  };
};
